/**
 * Smart Markdown editor:
 *   - Toolbar (Bold, Italic, H1/H2, UL, OL, Quote, Link, Code).
 *   - Auto-growing textarea (min/max rows).
 *   - Wraps the active selection with the requested syntax.
 *   - RTL/LTR aware. Mono font for clarity.
 */
import { useRef, useEffect, type ChangeEvent } from "react";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Quote, Link as LinkIcon, Code } from "lucide-react";

type Dir = "rtl" | "ltr";

interface Props {
  value: string;
  onChange: (next: string) => void;
  dir: Dir;
  label?: string;
  placeholder?: string;
  minRows?: number;
}

export function MarkdownEditor({ value, onChange, dir, label, placeholder, minRows = 8 }: Props) {
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.max(ta.scrollHeight, minRows * 24)}px`;
  }, [value, minRows]);

  const apply = (kind: string) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = value.slice(0, start);
    const sel = value.slice(start, end) || "";
    const after = value.slice(end);
    let inserted = sel;
    let cursorOffset = 0;

    switch (kind) {
      case "bold":
        inserted = `**${sel || "نص"}**`;
        cursorOffset = sel ? inserted.length : 2 + 3;
        break;
      case "italic":
        inserted = `*${sel || "نص"}*`;
        cursorOffset = sel ? inserted.length : 1 + 3;
        break;
      case "h1":
        inserted = `\n# ${sel || "عنوان"}\n`;
        cursorOffset = inserted.length;
        break;
      case "h2":
        inserted = `\n## ${sel || "عنوان فرعي"}\n`;
        cursorOffset = inserted.length;
        break;
      case "ul":
        inserted = (sel || "عنصر 1\nعنصر 2").split("\n").map((l) => `- ${l}`).join("\n");
        inserted = `\n${inserted}\n`;
        cursorOffset = inserted.length;
        break;
      case "ol":
        inserted = (sel || "عنصر 1\nعنصر 2").split("\n").map((l, i) => `${i + 1}. ${l}`).join("\n");
        inserted = `\n${inserted}\n`;
        cursorOffset = inserted.length;
        break;
      case "quote":
        inserted = (sel || "اقتباس").split("\n").map((l) => `> ${l}`).join("\n");
        inserted = `\n${inserted}\n`;
        cursorOffset = inserted.length;
        break;
      case "code":
        inserted = `\`${sel || "code"}\``;
        cursorOffset = inserted.length;
        break;
      case "link": {
        const url = typeof window !== "undefined" ? window.prompt("URL", "https://") : "https://";
        if (!url) return;
        inserted = `[${sel || "نص الرابط"}](${url})`;
        cursorOffset = inserted.length;
        break;
      }
    }

    const next = before + inserted + after;
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + cursorOffset;
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value);

  const Btn = ({ kind, Icon, title }: { kind: string; Icon: typeof Bold; title: string }) => (
    <button
      type="button"
      onClick={() => apply(kind)}
      title={title}
      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-background focus-within:ring-2 focus-within:ring-primary transition">
      {label && (
        <div className="px-3 pt-2 text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          {label}
        </div>
      )}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border/60 bg-card/40 flex-wrap">
        <Btn kind="bold" Icon={Bold} title="Bold (**)" />
        <Btn kind="italic" Icon={Italic} title="Italic (*)" />
        <span className="w-px h-4 bg-border mx-1" />
        <Btn kind="h1" Icon={Heading1} title="Heading 1 (#)" />
        <Btn kind="h2" Icon={Heading2} title="Heading 2 (##)" />
        <span className="w-px h-4 bg-border mx-1" />
        <Btn kind="ul" Icon={List} title="Bulleted list (-)" />
        <Btn kind="ol" Icon={ListOrdered} title="Numbered list (1.)" />
        <Btn kind="quote" Icon={Quote} title="Quote (>)" />
        <span className="w-px h-4 bg-border mx-1" />
        <Btn kind="link" Icon={LinkIcon} title="Link [..](url)" />
        <Btn kind="code" Icon={Code} title="Inline code (`)" />
      </div>
      <textarea
        ref={taRef}
        dir={dir}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={minRows}
        spellCheck
        className="w-full p-3 text-sm bg-background focus:outline-none leading-relaxed font-mono resize-none"
        style={{ direction: dir }}
      />
    </div>
  );
}
